import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import type { JobDetail } from '@nest-recruit/shared';
import { Skeleton } from '../../../../components/Skeleton';
import { toneClass } from '../../../../components/Tone';
import { displayTimeZone, formatDateTime } from '../../../../lib/format';
import { useCanView } from '../../../../stores/session';
import { useJobHeaderCounts } from '../../api/jobs-api';
import { appliedTileOf, calendarDayOf, deadlineTileOf, runningTileOf, type SummaryTile } from '../../job-summary';
import styles from './JobSummary.module.css';

// Workflow review D1: three numbers beside the job's title — days running, the deadline and how many have applied. With no
// target close date the deadline says "No deadline" and, for someone who may change the job, links to where it is set (D1-a).
// "Applied" needs the right to see applications, as the stage bar does. Owner 2026-11-01 (design A, "one strip"): one card with
// a cell for each. Evening review 2026-11-01 item 14: no colour bar on the cells' top edge any more (it read as a stray box).

export function JobSummary({ detail }: { readonly detail: JobDetail }): ReactNode {
  const canSeeApplications = useCanView('application');
  const counts = useJobHeaderCounts(detail.id, canSeeApplications);
  const timeZone = displayTimeZone();
  const today = calendarDayOf(new Date(), timeZone);
  const running = runningTileOf(detail, today, timeZone);
  const deadline = deadlineTileOf(detail, today);
  const canSetDeadline = deadline.isMissing && detail.actions.includes('edit');
  return (
    <ul className={styles.strip} aria-label="This job at a glance">
      <Tile tile={running} title={detail.openedAt ? `Opened ${formatDateTime(detail.openedAt)}` : undefined} />
      <Tile tile={deadline}>
        {canSetDeadline && (
          <Link to="/jobs/$jobId/setup/$section" params={{ jobId: detail.id, section: 'basics' }} className={styles.action}>
            Set a deadline
          </Link>
        )}
      </Tile>
      {canSeeApplications && (
        <li className={`${toneClass} ${styles.cell}`} data-tone={appliedTileOf(undefined).tone}>
          <Link to="/jobs/$jobId/$tab" params={{ jobId: detail.id, tab: 'pipeline' }} className={styles.cellLink} aria-label={`Applied: ${appliedTileOf(counts.data?.applied).value}. See everyone on this job`}>
            <span className={styles.label}>Applied</span>
            <span className={styles.figure}>{counts.data ? appliedTileOf(counts.data.applied).value : <Skeleton width="3ch" />}</span>
            <span className={styles.meta}>See everyone</span>
          </Link>
        </li>
      )}
    </ul>
  );
}

function Tile({ tile, title, children }: { readonly tile: SummaryTile; readonly title?: string; readonly children?: ReactNode }): ReactNode {
  return (
    <li className={`${toneClass} ${styles.cell}`} data-tone={tile.tone} title={title}>
      <span className={styles.label}>{tile.label}</span>
      <Value value={tile.value} />
      {tile.meta !== null && <span className={styles.meta}>{tile.meta}</span>}
      {children}
    </li>
  );
}

/** "36 days over" reads as a big 36 and "days over" beside it; words alone ("No deadline", "Due today") stay at one size. */
function Value({ value }: { readonly value: string }): ReactNode {
  const parts = /^(\d[\d,]*)\s+(.+)$/.exec(value);
  if (!parts) return <span className={styles.words}>{value}</span>;
  return (
    <span>
      <span className={styles.figure}>{parts[1]}</span>
      <span className={styles.unit}>{parts[2]}</span>
    </span>
  );
}
